'use client';

import DEFAULT_OPERATIONS from '@/components/CreateAccount/createAccount.gql';
import { GET_ITEM_COUNT_QUERY } from '@/components/Header/cartTrigger.gql';
import StoreConfig from '@/config';
import { mittInstance } from '@/constants/mitt-instance';
import { useCartContext } from '@/context/cart';
import { useUserContext } from '@/context/user';
import { BrowserPersistence } from '@/util';
import { ApolloClient, HttpLink, InMemoryCache, useLazyQuery, useMutation } from '@apollo/client';
import qs from 'qs';
import { useEffect } from 'react';

const storage = new BrowserPersistence();

export const useSyncCart = () => {
  const [
    { cartId },
    {
      actions: { setCartId },
    },
  ] = useCartContext();

  const [{ isSignedIn }, { getUserDetails, setToken }] = useUserContext();

  const { mergeCartsMutation, mergeGuestCartsMutation, switchStoreMutation } = DEFAULT_OPERATIONS;

  const [mergeCarts] = useMutation(mergeCartsMutation);

  const [mergeGuestCarts] = useMutation(mergeGuestCartsMutation);

  const [fetchItemCount] = useLazyQuery(GET_ITEM_COUNT_QUERY, { fetchPolicy: 'cache-and-network' });

  const currentCountryCode = StoreConfig.current_country_code;

  useEffect(() => {
    const syncCarts = async () => {
      const queryString = window.location.search;
      const parsedParams = qs.parse(queryString, { ignoreQueryPrefix: true });

      const { cart_id, from_country, token } = parsedParams;
      const storageCartId = storage.getItem('cartId');

      if (cart_id && from_country && from_country !== currentCountryCode) {
        // 购物车有产品，需要执行购物车合并逻辑
        if (isSignedIn) {
          // 当前站点已登录，直接合并购物车
          await mergeCarts({
            variables: {
              destinationCartId: cartId || storageCartId,
              sourceCartId: cart_id,
            },
          });
        } else {
          if (token && !isSignedIn) {
            // 同步登录状态
            await setToken(token);
            await getUserDetails();
          }

          let cartCount = 0;

          if (cartId || storageCartId) {
            //  同步获取购物车数量
            const { data } = await fetchItemCount({
              variables: {
                cartId: cartId || storageCartId,
              },
            });

            cartCount = data?.cart?.total_quantity || 0;
          }

          const headers = {
            store: from_country,
            authorization: token ? `Bearer ${token}` : '',
          };

          const client = new ApolloClient({
            link: new HttpLink({
              uri: `${process.env.NEXT_PUBLIC_BASE_PATH}/api/graphql`,
              headers,
            }),
            cache: new InMemoryCache(),
          });

          if (cartCount === 0) {
            //  当前站点购物车为空，切换购物车
            await client.mutate({
              mutation: switchStoreMutation,
              variables: {
                cart_id: cart_id,
                store_code: currentCountryCode,
              },
            });

            // 修改cartId
            storage.setItem('cartId', cart_id);
            setCartId(cart_id);
          } else {
            //  当前站点购物车不为空，合并购物车

            await mergeGuestCarts({
              variables: {
                sourceCartId: cart_id,
                targetCartId: cartId || storageCartId,
              },
            });
          }
        }

        const url = new URL(window.location.href);

        url.searchParams.delete('from_country');
        url.searchParams.delete('cart_id');
        url.searchParams.delete('token');

        mittInstance.emit('REFRESH_CART_TRIGGER_ITEM_COUNT');
        window.history.replaceState({}, '', url);
      } else if (from_country && from_country !== currentCountryCode) {
        // 不需要执行购物车合并逻辑
        if (token && !isSignedIn) {
          // 同步登录状态
          await setToken(token);
          await getUserDetails();
        }

        const url = new URL(window.location.href);

        url.searchParams.delete('from_country');
        url.searchParams.delete('cart_id');
        url.searchParams.delete('token');

        window.history.replaceState({}, '', url);
      }
    };

    syncCarts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
